import type { EmployeeProfileResponse, RoleFitResponse } from '../../api/types';
import { useApiResource } from '../../hooks/useApiResource';
import { formatTimestamp } from './attributeDisplay';
import { FactSection } from './FactSection';
import { RoleFitBreakdown } from '../role/RoleFitBreakdown';

/**
 * Current-role Role Fit on the employee profile
 * (docs/UX_ARCHITECTURE.md §3.6, scoped to the MVP question).
 *
 * ---------------------------------------------------------------------
 * ONE ROLE, AND IT IS NOT CHOSEN HERE
 *
 * The role compared against is read from the employee's own profile
 * response — `currentRole.id`, resolved from their active RoleAssignment.
 * There is no picker, no search and no way for a reader to point this at
 * another role, and that is structural rather than a UI omission:
 * `docs/MVP_SCOPE.md` §2 scopes Role Fit to the current role, and
 * `docs/UX_ARCHITECTURE.md` §3.6 defers "browse fit against any role" to
 * post-MVP. The question this section answers is "is this person well
 * fitted to the role they are already in", which is a sanity check, not a
 * candidate search.
 *
 * `docs/ROLE_FRAMEWORK.md` §5 is the reason that boundary matters: Role Fit
 * is not a ranking of people, and the moment a profile can be pointed at
 * arbitrary roles it starts to read like one.
 *
 * NO CURRENT ROLE
 *
 * An employee may have no current role — `docs/DOMAIN_MODEL.md` §2.4 says
 * "at most one", not "exactly one". When that is the case no request is
 * made at all (there is nothing to compare against), and the section says
 * so plainly. That is a fact about organisational assignment, not a finding
 * about the person, and the copy says that too rather than leaving a blank
 * where a score would be.
 * ---------------------------------------------------------------------
 */
export function RoleFitSection({ profile }: { profile: EmployeeProfileResponse }) {
  const currentRole = profile.currentRole;

  // The hook is called unconditionally, as hooks must be; a null path makes
  // it inert, and the branch below never consults it in that case.
  const fit = useApiResource<RoleFitResponse>(
    currentRole ? `/employees/${profile.id}/role-fit/${currentRole.id}` : null
  );

  return (
    <FactSection<RoleFitResponse | null>
      id="role-fit"
      title="Role fit"
      // With no current role there is nothing to request and nothing to
      // wait for: the answer is already known from the profile response.
      state={currentRole ? fit : { status: 'loaded', data: null }}
      loadingLabel="Loading role fit…"
      errorTitle="Role fit could not be loaded"
      isEmpty={(data) => data === null}
      empty={{
        title: 'No current role is recorded',
        detail:
          'Role fit compares a person against the requirements of the role they hold, so there is nothing to compare against. That is an absence of a recorded role assignment, not a finding about this person.',
      }}
      summary={(data) =>
        currentRole && data ? `Against ${currentRole.name} · as at ${formatTimestamp(data.asOf)}` : ''
      }
      note="How this person's recorded values compare with the requirements of the role they currently hold. It is a comparison against one role's thresholds — not a score of the person, and not a comparison with anyone else."
    >
      {/* Non-null by construction: `isEmpty` already returned false, which
          it only can when a current role exists and the request succeeded. */}
      {(data) => <RoleFitBreakdown fit={data!} roleName={currentRole!.name} roleId={currentRole!.id} />}
    </FactSection>
  );
}
